import React, { useEffect, useState } from 'react';

import { useForm, zodResolver } from '@mantine/form';
import { t } from 'i18next';
import { IconAt } from '@tabler/icons-react';
import * as z from 'zod';
import { inject, observer } from 'mobx-react';
import { AppMedError } from '../../../global/error/AppMedError.ts';

import { useNavigate } from 'react-router-dom';
import { ICollaboratorPayload } from '../../../global/entities/Collaborator/Collaborator.ts';
import { Button, Flex, Group, Notification, Paper, Select, Stack, TextInput } from '@mantine/core';
import { usePutCollaborator } from '../../../global/entities/Collaborator/mutations/usePutCollaboratorMutations.tsx';
import { useGetCollaboratorById } from '../../../global/entities/Collaborator/queries/useGetCollaboratorById.tsx';
import { useSessionProvider } from '../../../global/provider/sessionContext.tsx';
import { useGetTaskSummaries } from '../../../global/entities/Tasks/queries/useGetTaskSummaries.tsx';
import { useParams } from 'react-router';
import { PatientStore } from '../../../global/Store/PatientStore.ts';
import { RouteStore } from '../../../global/Store/RouteStore.ts';
import { useGetCollaboratorNew } from '../../../global/entities/Collaborator/queries/useGetCollaboratorNew.tsx';
import { usePostCollaboratorMutations } from '../../../global/entities/Collaborator/mutations/usePostCollaboratorMutations.tsx';
import { useGetPatientCollaborators } from '../../../global/entities/Collaborator/queries/useGetPatientCollaborators.tsx';
import { useEmailErrorHandler } from '../../../global/hooks/useEmailErrorHandler.ts';
import { EmailErrorCode } from '../../../global/types/EmailErrorTypes.ts';

export type ICollaboratorFormProps = {
    patientStore?: PatientStore;
    routeStore?: RouteStore;
};

const CollaboratorForm: React.FC<ICollaboratorFormProps> = inject('routeStore')(
    observer(({ routeStore }) => {
        const [error, setError] = useState<string | null>(null);
        const [isSubmitting, setIsSubmitting] = useState(false);
        const navigate = useNavigate();
        const { currentUser } = useSessionProvider();
        const { id } = useParams<{ id: string }>();

        const putCollaborator = usePutCollaborator();
        const postCollaborator = usePostCollaboratorMutations();
        const { refetch: refetchCollaborators } = useGetPatientCollaborators();
        const { handleEmailError } = useEmailErrorHandler();

        const { refetch: refetchTaskSummaries } = useGetTaskSummaries();
        const { data: collaborator } = useGetCollaboratorById();
        const { data: collaboratorNew } = useGetCollaboratorNew();

        const schema = z.object({
            firstName: z.string().min(1, { message: t('formError.firstName') }),
            lastName: z.string().min(1, { message: t('formError.lastName') }),
            role: z.string().min(1, { message: t('formError.roleIsRequired') }),
            preferredLanguage: z.preprocess(
                (val) => (val === null ? '' : val),
                z.string().min(1, { message: t('formError.languageIsRequired') }),
            ),
            email: z.string().min(1, { message: t('formError.emailIsRequired') }),
        });

        const form = useForm({
            initialValues: {
                firstName: '',
                lastName: '',
                email: '',
                role: '',
                preferredLanguage: '',
            },
            validate: zodResolver(schema),
        });

        const handleCancel = () => {
            form.reset();
            setIsSubmitting(false);
            if (routeStore?.lastRoute) 
                navigate(routeStore.lastRoute);
            
        };

        const handleSubmit = async (formValues: ICollaboratorPayload) => {
            setError(null);
            setIsSubmitting(true);

            if (!currentUser?.patientID || !id) 
                return;

            const payload = {
                patientID: parseInt(currentUser.patientID),
                firstName: formValues.firstName,
                lastName: formValues.lastName,
                email: formValues.email,
                role: formValues.role,
                preferredLanguage: formValues.preferredLanguage,
            };

            try {
                if (id === '0') 
                    await postCollaborator.mutateAsync(payload);
                 else 
                    await putCollaborator.mutateAsync(payload);
                
                handleCancel();
            } catch (e) {
                if (e instanceof AppMedError) {
                    const error = handleEmailError(e);
                    if (error?.errorCode === EmailErrorCode.EMAIL_ADDRESS_UNUSABLE) 
                        setError(t('formError.collaboratorEmailUnusable'));
                     else 
                        setError(error?.message || 'An unexpected error occurred.');
                    
                } else 
                    setError('An unexpected error occurred.');
                
                setIsSubmitting(false);

            } finally {
                refetchTaskSummaries();
                refetchCollaborators();
            }
        };

        useEffect(() => {
            if (collaborator) {
                form.reset();
                form.setValues(collaborator);
            } else 
                form.reset();
            
        }, [collaborator]);

        return (
            <Paper h={'min-content'} w={'100%'}>
                <form
                    onSubmit={form.onSubmit((values) => {
                        handleSubmit(values);
                    })}
                >
                    <Stack w={'100%'} gap={'md'}>
                        <Group grow w={'100%'}>
                            <TextInput
                                label={t('formGlobal.firstName')}
                                placeholder={t('formGlobal.enterFirstName')}
                                {...form.getInputProps('firstName')}
                                withAsterisk
                                w={'50%'}
                            />
                            <TextInput
                                label={t('formGlobal.lastName')}
                                placeholder={t('formGlobal.enterLastName')}
                                {...form.getInputProps('lastName')}
                                withAsterisk
                                w={'50%'}
                            />
                        </Group>
                        <Group grow w={'100%'}>
                            <Select
                                label={t('formGlobal.language')}
                                placeholder={t('formGlobal.selectLanguage')}
                                data={collaboratorNew?.languagesCombobox}
                                w={'50%'}
                                {...form.getInputProps('preferredLanguage')}
                            />
                            <TextInput
                                // disabled={id !== '0'}
                                label={t('formTask.collaboratorRole')}
                                placeholder={t('formGlobal.enterRole')}
                                w={'50%'}
                                {...form.getInputProps('role')}
                            />
                        </Group>
                        <TextInput
                            label={t('formGlobal.email')}
                            placeholder={t('formGlobal.enterEmailAddress')}
                            leftSection={<IconAt size={16} />}
                            {...form.getInputProps('email')}
                            withAsterisk
                        />

                        <Flex mt="md" justify={'flex-end'} gap={'md'}>
                            <Button ml={'auto'} variant="default" onClick={handleCancel}>
                                {t('button.cancel')}
                            </Button>
                            <Button loading={isSubmitting} type="submit">
                                {t('button.save')}
                            </Button>
                        </Flex>
                    </Stack>
                </form>
                {error && (
                    <Notification color="red" onClose={() => setError(null)}>
                        {error}
                    </Notification>
                )}
            </Paper>
        );
    }),
);

export default CollaboratorForm;
